/**
 * What this tab holds while it is connected: the dataKey that opens the
 * vault, and the grant secret that gets past the gate.
 *
 * Memory only, by design. Nothing is written to localStorage, IndexedDB or a
 * cookie, so closing the tab ends the session and a reload means scanning
 * again. "Remember this browser" is a later step that arrives with
 * KMS-wrapped custody, not something to approximate with a storage call — a
 * key in localStorage is readable by any script that ever lands on this
 * origin, which is precisely what the origin split and the CSP exist to
 * prevent.
 */

export interface VaultSession {
  dataKey: Uint8Array;
  grantSecret: Uint8Array;
}

let current: VaultSession | null = null;
const listeners = new Set<(session: VaultSession | null) => void>();

export function getSession(): VaultSession | null {
  return current;
}

export function setSession(session: VaultSession): void {
  current = session;
  for (const listener of listeners) listener(current);
}

/** Zeroes the key material before dropping it. Not a guarantee — the engine
 *  may have copied it — but it shortens the window a heap snapshot sees. */
export function clearSession(): void {
  if (current) {
    current.dataKey.fill(0);
    current.grantSecret.fill(0);
  }
  current = null;
  for (const listener of listeners) listener(null);
}

export function subscribe(listener: (session: VaultSession | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
